import { UserModel } from '../modules/auth/core/_models'

/**
 * Resolves the primary landing route for the authenticated user based on RBAC allowed menus.
 * - If the user has dashboard permission or is an Admin/SuperAdmin, returns '/dashboard'.
 * - If not, falls back to the highest priority module they are permitted to access.
 * - If no modules are permitted, safely lands on '/profile'.
 */
export const getDefaultLandingRoute = (user?: UserModel | null): string => {
  if (!user) return '/auth/login'

  const roleName = user.role?.name?.toUpperCase()
  // SuperAdmin and Admin have full system access
  if (roleName === 'ADMIN' || roleName === 'SUPERADMIN') {
    return '/dashboard'
  }

  const rawMenus = user.role?.allowedMenus || ''
  const allowed = rawMenus
    .split(',')
    .map((m) => m.trim().toLowerCase())
    .filter(Boolean)

  // 1. Dashboard (Primary choice if permitted)
  if (allowed.includes('dashboard')) {
    return '/dashboard'
  }

  // 2. Clients
  if (allowed.includes('clients')) {
    return '/clients'
  }

  // 3. Billing Rates
  if (allowed.includes('billing_rates')) {
    return '/billing'
  }

  // 4. CDR Report
  if (allowed.includes('cdr_report')) {
    return '/cdr'
  }

  // 5. Invoice
  if (allowed.includes('invoice')) {
    return '/invoice'
  }

  // 6. Activity Log (Permitted by menu key or default tenant CLIENT role)
  if (allowed.includes('activity_log') || roleName === 'CLIENT') {
    return '/activity-log'
  }

  // 7. Global Settings
  if (allowed.includes('global_settings')) {
    return '/settings'
  }

  // 8. Role Management
  if (allowed.includes('role_management')) {
    return '/roles'
  }

  // 9. User Management
  if (allowed.includes('user_management')) {
    return '/users'
  }

  // Fallback: Profile page (always safe and accessible to any authenticated user)
  return '/profile'
}
